import { Chip, Tooltip, Typography } from "@mui/material";
import {
  CheckCircleOutline as ApprovedIcon,
  HourglassEmpty as PendingIcon
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { OnboardingRequestStatus } from "../../api/Api";
import { OnboardingRequestBook, OnboardingRequestRowBase } from "./types";

/**
 * The columns every onboarding request grid is built from. Both the admin review grid and the
 * requester's own list use these, so a change to how a status or a timestamp reads lands in all six
 * places at once.
 */

export const statusColor = (status?: OnboardingRequestStatus) => {
  if (status === "Approved") return "success";
  if (status === "Pending") return "warning";
  return "default";
};

const StatusIcon = ({ status }: { status?: OnboardingRequestStatus }) => {
  if (status === "Approved") return <ApprovedIcon fontSize="small" />;
  if (status === "Pending") return <PendingIcon fontSize="small" />;
  return null;
};

export const requesterColumn = <
  TRow extends OnboardingRequestRowBase
>(): GridColDef<TRow> => ({
  field: "requestedBy",
  headerName: "Requester",
  minWidth: 220,
  flex: 1,
  valueGetter: (_v, row) => row.requestedByDisplayName || row.requestedBy || ""
});

export const entityNameColumn = <TRow extends OnboardingRequestRowBase>(
  book: OnboardingRequestBook<TRow>
): GridColDef<TRow> => ({
  field: "entityName",
  headerName: book.entityLabel,
  minWidth: 240,
  flex: 1,
  valueGetter: (_v, row) => book.entityName(row)
});

export const submittedColumn = <
  TRow extends OnboardingRequestRowBase
>(): GridColDef<TRow> => ({
  field: "requestedAt",
  headerName: "Submitted",
  width: 220,
  valueGetter: (_v, row) =>
    row.requestedAt ? new Date(row.requestedAt) : null,
  valueFormatter: (value: Date | null) =>
    value ? dayjs(value).format("LLL") : ""
});

export const statusColumn = <TRow extends OnboardingRequestRowBase>(
  // The admin grid shows an icon alongside the label, the requester's own list does not.
  withIcon = false
): GridColDef<TRow> => ({
  field: "status",
  headerName: "Status",
  width: withIcon ? 140 : 130,
  renderCell: (params) => (
    <Chip
      size="small"
      icon={withIcon ? <StatusIcon status={params.row.status} /> : undefined}
      label={params.row.status ?? "Pending"}
      color={statusColor(params.row.status)}
    />
  )
});

export const reviewerCommentColumn = <
  TRow extends OnboardingRequestRowBase
>(): GridColDef<TRow> => ({
  field: "reviewerComment",
  headerName: "Comment",
  minWidth: 220,
  flex: 1,
  renderCell: (params) =>
    params.row.reviewerComment ? (
      <Tooltip title={params.row.reviewerComment}>
        <Typography variant="body2" noWrap>
          {params.row.reviewerComment}
        </Typography>
      </Tooltip>
    ) : null
});
